import { Shop } from "../types/shop";
import { GooglePlaceWithDistance } from "../types/googlePlaces";

/**
 * Cleans a phone number by keeping only the + at the beginning (if present) and numbers
 * @param phoneNumber - Phone number string to clean
 * @returns Cleaned phone number (e.g., "+1 (555) 123-4567" -> "+15551234567")
 */
export const cleanPhoneNumber = (
  phoneNumber: string | null | undefined
): string | null => {
  if (!phoneNumber) return null;

  // Check if it starts with +
  const hasPlus = phoneNumber.trim().startsWith("+");

  // Remove all non-numeric characters
  const numbersOnly = phoneNumber.replace(/\D/g, "");

  // If it originally had a +, add it back
  return hasPlus ? `+${numbersOnly}` : numbersOnly;
};

/**
 * Converts a Google Place to a Shop model for consistent display
 * @param place - Google Place data from Places API
 * @returns Shop object with mapped properties
 */
export const convertGooglePlaceToShop = (
  place: GooglePlaceWithDistance
): Shop => {
  // Generate a unique negative ID for Google places to avoid conflicts with trusted shops
  const googleId = parseInt(place.place_id.replace(/\D/g, "")) || 0;

  // Create a more descriptive description based on types
  const typeDescriptions = place.types
    .filter((type) => !["establishment", "point_of_interest"].includes(type))
    .map((type) =>
      type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    )
    .join(", ");

  const description = typeDescriptions
    ? `Business type: ${typeDescriptions}`
    : "Local business";

  return {
    id: -Math.abs(googleId), // Use negative ID to avoid conflicts with trusted shops
    name: place.name,
    address: place.vicinity,
    latitude: place.geometry.location.lat,
    longitude: place.geometry.location.lng,
    email: null, // Google Places doesn't provide email
    phoneNumber: cleanPhoneNumber(
      place.formatted_phone_number || place.international_phone_number || null
    ),
    description: description,
    workingHours: place.opening_hours?.weekday_text?.join(", ") || null,
    isActive: place.opening_hours?.open_now ?? false, // Only assume open if explicitly indicated
    preference: 0, // Google Places are treated as Trusted (0) by default
    createdAt: new Date().toISOString(),
    updatedAt: null,
    rating: place.rating, // Include Google Places rating
    userRatingsTotal: place.user_ratings_total, // Include total number of ratings
    website: place.website, // Include website if available
    isGooglePlace: true, // Flag to identify Google Places results
    distance: formatGooglePlaceDistance(place), // Include distance information
    duration: formatGooglePlaceDuration(place), // Include duration information
    placeId: place.place_id, // Store place_id for lazy loading details
  };
};

/**
 * Formats distance display for Google Places results
 * @param place - Google Place with distance data
 * @returns Formatted distance string
 */
export const formatGooglePlaceDistance = (
  place: GooglePlaceWithDistance
): string => {
  if (place.roadDistance) {
    return place.roadDistance.distance.text;
  }

  // Fallback to aerial distance
  const aerialMiles = Math.round((place.aerialDistance / 1609.34) * 10) / 10;
  return `${aerialMiles} mi (aerial)`;
};

/**
 * Formats duration display for Google Places results
 * @param place - Google Place with distance data
 * @returns Formatted duration string
 */
export const formatGooglePlaceDuration = (
  place: GooglePlaceWithDistance
): string => {
  return place.roadDistance ? place.roadDistance.duration.text : "N/A";
};

/**
 * Checks if a shop has contact information
 * @param shop - Shop object to check
 * @returns True if shop has phone, email, or website
 */
export const hasContactInfo = (shop: Shop): boolean => {
  return !!(shop.phoneNumber || shop.email || shop.website);
};

/**
 * Formats working hours for display
 * @param workingHours - Working hours string
 * @returns Formatted working hours or null if not available
 */
export const formatWorkingHours = (
  workingHours: string | null
): string | null => {
  if (!workingHours) return null;

  // Clean up the working hours format
  return workingHours
    .split(", ")
    .map((day) => day.trim())
    .filter((day) => day.length > 0)
    .join(", ");
};

/**
 * Shortens time format for compact display
 * @param timeStr - Time string to shorten
 * @returns Shortened time string
 */
export const shortenTime = (timeStr: string): string => {
  if (!timeStr) return "";
  // Convert "12 mins" to "12m", "1 hour 30 mins" to "1h 30m"
  return timeStr
    .replace(/\s+hours?/g, "h")
    .replace(/\s+minutes?/g, "m")
    .replace(/\s+mins?/g, "m")
    .replace(/\s+hour\s+/g, "h ")
    .trim();
};

/**
 * Formats working hours in a more compact and readable format
 * @param workingHours - Working hours string from Google Places
 * @returns Formatted working hours with ranges like "MON-FRI 8am-6pm"
 */
export const formatWorkingHoursCompact = (
  workingHours: string | null
): string | null => {
  if (!workingHours) return null;

  try {
    // Split by comma and clean up
    const days = workingHours
      .split(", ")
      .map((day) => day.trim())
      .filter((day) => day.length > 0);

    if (days.length === 0) return null;

    // Group consecutive days with same hours
    const groupedDays: { [key: string]: string[] } = {};

    days.forEach((day) => {
      // Extract day and hours (e.g., "Monday: 8:00 AM – 6:00 PM" -> "Monday" and "8:00 AM – 6:00 PM")
      const match = day.match(/^([^:]+):\s*(.+)$/);
      if (match) {
        const dayName = match[1].trim();
        let hours = match[2].trim();

        // Handle special cases
        if (
          hours.toLowerCase().includes("24 hours") ||
          hours.toLowerCase().includes("open 24")
        ) {
          hours = "24 hours";
        } else if (hours.toLowerCase().includes("closed")) {
          hours = "Closed";
        } else {
          // Normalize time format: "9:00 am – 8:00 pm" -> "9am-8pm"
          hours = hours
            .replace(/\s+/g, "") // Remove all spaces
            .replace(/:00/g, "") // Remove :00
            .replace(/–/g, "-") // Replace en dash with hyphen
            .toLowerCase(); // Convert to lowercase
        }

        if (!groupedDays[hours]) {
          groupedDays[hours] = [];
        }
        groupedDays[hours].push(dayName);
      }
    });

    // Format grouped days
    const formattedGroups = Object.entries(groupedDays).map(
      ([hours, dayList]) => {
        // Convert day names to abbreviations
        const dayAbbrevs = dayList.map((day) => {
          const dayMap: { [key: string]: string } = {
            Monday: "MON",
            Tuesday: "TUE",
            Wednesday: "WED",
            Thursday: "THU",
            Friday: "FRI",
            Saturday: "SAT",
            Sunday: "SUN",
          };
          return dayMap[day] || day.substring(0, 3).toUpperCase();
        });

        // Sort days by their order in the week
        const dayOrder = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
        dayAbbrevs.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

        if (dayList.length === 1) {
          return `${dayAbbrevs[0]}: ${hours}`;
        } else if (dayList.length === 2) {
          return `${dayAbbrevs.join(", ")}: ${hours}`;
        } else {
          // Check if it's consecutive days (Monday-Friday pattern)
          const isConsecutive = dayAbbrevs.every((day, index) => {
            if (index === 0) return true;
            const prevDayIndex = dayOrder.indexOf(dayAbbrevs[index - 1]);
            const currentDayIndex = dayOrder.indexOf(day);
            return currentDayIndex === prevDayIndex + 1;
          });

          if (isConsecutive && dayList.length >= 3) {
            return `${dayAbbrevs[0]}-${
              dayAbbrevs[dayAbbrevs.length - 1]
            }: ${hours}`;
          } else {
            return `${dayAbbrevs.join(", ")}: ${hours}`;
          }
        }
      }
    );

    return formattedGroups.join(" | ");
  } catch (error) {
    console.warn("Error formatting working hours:", error);
    return workingHours; // Fallback to original
  }
};

/**
 * Formats distance from meters to miles for display
 * @param meters - Distance in meters
 * @returns Formatted distance string (e.g., "2.3 mi" or "150m")
 */
export const formatDistance = (meters: number): string => {
  const miles = meters / 1609.34;
  if (miles < 0.1) {
    return `${Math.round(meters)}m`;
  }
  return `${miles.toFixed(1)}mi`;
};

/**
 * Merges aerial and driving distance results
 * For top 10 shops: uses driving distance if available, otherwise keeps aerial
 * For remaining shops: uses aerial distance only
 * @param aerialShops - Shops with aerial distance calculated
 * @param drivingResults - Results from distance matrix API
 * @returns Merged results with both aerial and driving distances where available
 */
export const mergeAerialAndDrivingDistances = (
  aerialShops: Array<{
    shop: any;
    aerialDistance: number;
    distanceText: string;
    drivingDistance?: string;
    drivingDuration?: string;
  }>,
  drivingResults: Array<{
    shopId: number;
    distance: { text: string };
    duration: { text: string };
    status: string;
  }>
): Array<{
  shop: any;
  aerialDistance: number;
  distanceText: string;
  drivingDistance?: string;
  drivingDuration?: string;
}> => {
  return aerialShops.map((aerialResult, index) => {
    // Only calculate driving distance for top 10 shops
    if (index < 10) {
      const drivingResult = drivingResults.find(
        (driving) => driving.shopId === aerialResult.shop.id
      );

      if (drivingResult?.status === "OK") {
        return {
          ...aerialResult,
          drivingDistance: drivingResult.distance.text,
          drivingDuration: drivingResult.duration.text,
        };
      }
    }

    // For shops beyond top 10, or if driving distance failed, use aerial distance
    return aerialResult;
  });
};
